import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calculator, ArrowRight } from "lucide-react";
import { generateBreadcrumbSchema } from "@/lib/structuredData";

const VAT_RATE = 0.15;

const fmt = (n: number) =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
  }).format(n);

export default function VATCalculator() {
  const [amount, setAmount] = useState("1000");
  const [mode, setMode] = useState<"add" | "extract">("add");

  const value = parseFloat(amount.replace(/,/g, "")) || 0;

  const result = useMemo(() => {
    if (mode === "add") {
      const vat = value * VAT_RATE;
      return { net: value, vat, gross: value + vat };
    } else {
      const net = value / (1 + VAT_RATE);
      const vat = value - net;
      return { net, vat, gross: value };
    }
  }, [value, mode]);

  const toolSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "South Africa VAT Calculator (15%)",
    applicationCategory: "FinanceApplication",
    operatingSystem: "Any",
    description:
      "Free South African VAT calculator. Add 15% VAT to a net amount or extract VAT from a VAT-inclusive price instantly.",
    url: "https://bookkeepingservices.co.za/tools/vat-calculator",
    offers: { "@type": "Offer", price: "0", priceCurrency: "ZAR" },
  };

  return (
    <Layout>
      <SEO
        title="VAT Calculator South Africa | Add or Remove 15% VAT | Free Tool"
        description="Free South African VAT calculator. Add 15% VAT to a net price or extract VAT from a VAT-inclusive amount. Instant, accurate and SARS-aligned."
        canonical="/tools/vat-calculator"
        structuredData={[
          toolSchema,
          generateBreadcrumbSchema([
            { name: "Home", url: "https://bookkeepingservices.co.za/" },
            { name: "Tools", url: "https://bookkeepingservices.co.za/tools" },
            { name: "VAT Calculator", url: "https://bookkeepingservices.co.za/tools/vat-calculator" },
          ]),
        ]}
      />

      <Breadcrumbs items={[{ name: "Tools", href: "/tools" }, { name: "VAT Calculator" }]} />

      <section className="py-12 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary mb-4">
                <Calculator className="w-7 h-7" />
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
                VAT Calculator South Africa
              </h1>
              <p className="text-lg text-muted-foreground">
                Add or remove 15% VAT in seconds. Built by SAICA registered accountants.
              </p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-6 md:p-10 shadow-md">
              <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-xl mb-6">
                <button
                  onClick={() => setMode("add")}
                  className={`py-3 rounded-lg text-sm font-semibold transition-all ${
                    mode === "add" ? "bg-card shadow-sm text-primary" : "text-muted-foreground"
                  }`}
                >
                  Add VAT (net → gross)
                </button>
                <button
                  onClick={() => setMode("extract")}
                  className={`py-3 rounded-lg text-sm font-semibold transition-all ${
                    mode === "extract" ? "bg-card shadow-sm text-primary" : "text-muted-foreground"
                  }`}
                >
                  Extract VAT (gross → net)
                </button>
              </div>

              <label className="block text-sm font-semibold text-foreground mb-2">
                {mode === "add" ? "Amount excluding VAT (ZAR)" : "Amount including VAT (ZAR)"}
              </label>
              <Input
                type="number"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-14 text-lg"
                placeholder="0.00"
                min="0"
              />

              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="text-center p-5 rounded-xl bg-muted">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Net</p>
                  <p className="font-heading text-lg md:text-xl font-bold text-foreground break-all">{fmt(result.net)}</p>
                </div>
                <div className="text-center p-5 rounded-xl bg-primary/10">
                  <p className="text-xs uppercase tracking-wide text-primary mb-2">VAT (15%)</p>
                  <p className="font-heading text-lg md:text-xl font-bold text-primary break-all">{fmt(result.vat)}</p>
                </div>
                <div className="text-center p-5 rounded-xl bg-foreground text-background">
                  <p className="text-xs uppercase tracking-wide opacity-70 mb-2">Total</p>
                  <p className="font-heading text-lg md:text-xl font-bold break-all">{fmt(result.gross)}</p>
                </div>
              </div>
            </div>

            <div className="mt-10 prose prose-sm max-w-none text-muted-foreground">
              <h2 className="font-heading text-2xl font-bold text-foreground">How South African VAT works</h2>
              <p>
                The standard VAT rate in South Africa is <strong>15%</strong>. Businesses with taxable turnover above R2,300,000 per year must
                register for VAT (compulsory threshold, effective 1 April 2026). Voluntary registration is available from R120,000 per year.
              </p>
              <ul>
                <li><strong>To add VAT:</strong> multiply the net amount by 1.15.</li>
                <li><strong>To extract VAT from a gross price:</strong> divide by 1.15 to get the net, then subtract.</li>
              </ul>
            </div>

            <div className="mt-10 bg-accent/40 border border-primary/20 rounded-2xl p-6 md:p-8 text-center">
              <h3 className="font-heading text-xl font-bold mb-2">Need help with VAT201 submissions?</h3>
              <p className="text-muted-foreground mb-5">
                Our Medium package includes bi-monthly VAT201 returns, registration and SARS audit support.
              </p>
              <Link to="/book-a-call">
                <Button className="btn-primary gap-2">
                  Talk to a Qualified Accountant
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
